import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useHistory, useLocation } from 'react-router';
import { useDispatch } from 'react-redux';
import { Controller, FormProvider, useForm } from 'react-hook-form';
import { Button, Form, FormGroup, Spinner } from 'reactstrap';
import intl from 'react-intl-universal';
import moment from 'moment-timezone';

import Icon from 'components/Icon';
import Page from 'components/Page';
import PageHeader from 'components/PageHeader';
import PageFooter from 'components/PageFooter';
import Checkbox from 'components/Checkbox';
import { useTypedSelector } from 'config/reducers';
import {
  togglePrivacyPolicyModal,
  toggleTermsOfServiceModal,
} from 'modules/forms/actions/form.actions';
import TermsOfServiceModal from 'modules/forms/components/TermsOfServiceModal';
import PrivacyPolicyModal from 'modules/forms/components/PrivacyPolicyModal';
import { EChallengeTypes, IChallengeMetadata } from 'modules/auth/interfaces/challenge.interfaces';
import r from 'modules/forms/constants/routes.constants';
import * as ActionTypes from 'modules/auth/actions/auth.actions';

interface IAcceptTermsForm {
  agreedTerms: boolean;
  session: string;
}

const PatientAcceptTerms = (): JSX.Element => {
  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation<{ session: string; metadata?: IChallengeMetadata }>();
  const { challenge: isLoading } = useTypedSelector(state => state.auth.loading);

  const mainRef = useRef<HTMLDivElement>(null);
  const [isScrolledFromTop, setScrolledFromTop] = useState(false);

  const methods = useForm<IAcceptTermsForm>({
    defaultValues: {
      agreedTerms: false,
      session: location.state?.session || '',
    },
    mode: 'onSubmit',
  });

  const { control, handleSubmit, setError, setValue } = methods;

  useEffect(() => {
    if (location.state?.session) {
      setValue('session', location.state.session);
    }
  }, [location.state?.session, setValue]);

  useEffect(() => {
    if (location && !location.state?.session) {
      history.push(r.patientSignIn);
    }
  }, [history, location]);

  useEffect(() => {
    const mainEl = mainRef.current;

    const handleScroll = () => {
      if (mainEl) {
        setScrolledFromTop(mainEl.scrollTop > 0);
      }
    };

    setTimeout(handleScroll, 50);
    mainEl?.addEventListener('scroll', handleScroll);

    return () => {
      mainEl?.removeEventListener('scroll', handleScroll);
    };
  }, []);

  const onSubmit = useCallback(
    (values: IAcceptTermsForm) => {
      const payload = {
        values: {
          session: values.session,
          challenge: EChallengeTypes.AGREEMENT_TERMS,
          agreedTerms: true,
          agreedTermsDate: moment().toISOString(),
        },
        setError: (message: string): void =>
          setError('agreedTerms', {
            message: intl.get(`auth.${message}`),
          }),
      };
      dispatch(ActionTypes.challenge(payload));
    },
    [dispatch, setError],
  );

  const onBack = () => {
    history.push(r.patientSignIn);
  };

  return (
    <Page className="accept-terms">
      <PageHeader
        title={intl.get('auth.termsAndConditions')}
        isScrolledFromTop={isScrolledFromTop}
      />
      <div className="page-content">
        <main ref={mainRef} id="page-main-scrollable" className="page-main">
          <FormProvider {...methods}>
            <Form className="accept-terms-form" onSubmit={handleSubmit(onSubmit)}>
              <Controller
                control={control}
                name="agreedTerms"
                render={({ field: { value, onChange } }) => (
                  <FormGroup className="mb-4">
                    <Checkbox
                      id="agreedTerms"
                      name="agreedTerms"
                      checked={value}
                      label={
                        <span>
                          {intl.get('auth.agreeWithTerms')}{' '}
                          <span
                            className="auth-resend-code resend-title accept-terms-form__link"
                            role="button"
                            tabIndex={0}
                            onKeyDown={e =>
                              e.key === 'Enter' && dispatch(toggleTermsOfServiceModal(true))
                            }
                            onClick={e => {
                              e.preventDefault();
                              dispatch(toggleTermsOfServiceModal(true));
                            }}
                          >
                            {intl.get('auth.termsOfService')}
                          </span>{' '}
                          {intl.get('common.and')}{' '}
                          <span
                            className="auth-resend-code resend-title accept-terms-form__link"
                            role="button"
                            tabIndex={0}
                            onKeyDown={e =>
                              e.key === 'Enter' && dispatch(togglePrivacyPolicyModal(true))
                            }
                            onClick={e => {
                              e.preventDefault();
                              dispatch(togglePrivacyPolicyModal(true));
                            }}
                          >
                            {intl.get('auth.privacyPolicy')}
                          </span>
                        </span>
                      }
                      onChange={e => onChange(e.target.checked)}
                    />
                  </FormGroup>
                )}
              />
              <Controller
                control={control}
                name="session"
                render={({ field }) => <input {...field} type="hidden" />}
              />
            </Form>
          </FormProvider>

          <PageFooter className="page-footer--scrollable">
            <Button color="primary" outline type="button" block onClick={onBack}>
              <Icon iconName="arrow-left" />
              {intl.get('button.back')}
            </Button>
            <Button
              color="primary"
              type="button"
              block
              disabled={isLoading || !methods.watch('agreedTerms')}
              onClick={handleSubmit(onSubmit)}
            >
              {isLoading && <Spinner size="sm" />}
              {intl.get('button.continue')}
            </Button>
          </PageFooter>
        </main>
      </div>

      <TermsOfServiceModal />
      <PrivacyPolicyModal />
    </Page>
  );
};

export default PatientAcceptTerms;
